import { useId, useState } from "react";
import type {
  NotificationStep,
  NotificationsResponse,
  ServedAction,
} from "@patent-studio/api-types";
import { Button, buttonChrome } from "@/components/ui/button";
import { IconTile } from "@/components/ui/IconTile";
import { Panel } from "@/components/ui/Panel";
import { plural } from "@/lib/plural";
import { cn } from "@/lib/utils";
import { MaybeLink } from "../layout/MaybeLink";
import { STEP_PLACES, comesBefore } from "./cadence";

const STAYS =
  "Your confirmed content stays as it is until you accept, amend or reject each change.";
const LATER = " Further reviews may follow in later sections.";

/** Show all's list: each change, a way to its record, and what stays as it is. */
function BannerList({
  here,
  held,
  onJump,
}: {
  here: ServedAction[];
  held: boolean;
  onJump: (actionId: string) => void;
}) {
  return (
    <div className="ml-7 mt-2 flex flex-col gap-0.5">
      {here.map((action) => (
        <Button
          key={action.action_id}
          variant="link"
          size="dense"
          className="justify-start"
          onClick={() => {
            onJump(action.action_id);
          }}
        >
          <span
            aria-hidden
            className="size-1.5 flex-none rounded-full bg-destructive"
          />
          {action.label}
          {/* v0.28's muted cue after each change; the button already says where it goes. */}
          <span aria-hidden className="font-medium text-subtle-foreground">
            Go to
          </span>
        </Button>
      ))}
      <p className="mt-1 text-[11.5px] leading-snug text-muted-foreground">
        {STAYS}
        {held && LATER}
      </p>
    </div>
  );
}

/** Whether a page's banner has anything to say: its own step's changes, or an earlier step's to go to first. */
export function bannerShows(
  view: NotificationsResponse,
  step: NotificationStep,
): boolean {
  const surfaced = view.surfaced;
  if (surfaced === null) return false;
  return surfaced === step
    ? view.actions.some((action) => action.step === step)
    : comesBefore(surfaced, step);
}

/**
 * On a page after the surfaced step: how many changes wait there, and the way
 * to them. Changes found after a later stage moved on surface on an earlier
 * page the attorney may not return to, so the page they are on says so.
 */
function EarlierChanges({
  view,
  surfaced,
  taskId,
  className,
}: {
  view: NotificationsResponse;
  surfaced: NotificationStep;
  taskId: string;
  className?: string;
}) {
  const id = useId();
  const count = view.steps.find((held) => held.step === surfaced)?.pending ?? 0;
  const { name, to } = STEP_PLACES[surfaced];
  return (
    <Panel
      role="region"
      aria-labelledby={id}
      pad="snug"
      className={cn("border-primary/20 shadow-lg", className)}
    >
      <div className="flex min-h-7 items-center gap-2.5">
        <IconTile tone="alert" shape="round" size="xs" numeral={count} />
        <b id={id} className="min-w-0 flex-1 text-[13px]">
          {`${plural(count, "suggested change")} to ${name}`}
        </b>
        <MaybeLink to={to(taskId)} className={buttonChrome({ size: "dense" })}>
          {`Go to ${name}`}
        </MaybeLink>
      </div>
    </Panel>
  );
}

/**
 * The banner over the surfaced step's changes, as v0.28 draws it above the
 * stage's footer: the count, the new data the changes came from, Show all and
 * Next change. Later steps are held until the earlier one is reviewed (lane M
 * decision 21), so a later page points to the earlier step instead; a page
 * before the surfaced step has nothing to show.
 */
export function ChangesBanner({
  view,
  step,
  taskId,
  onJump,
  className,
}: {
  view: NotificationsResponse;
  step: NotificationStep;
  taskId: string;
  onJump: (actionId: string) => void;
  className?: string;
}) {
  const id = useId();
  const [open, setOpen] = useState(false);
  const here = view.actions.filter((action) => action.step === step);
  if (!bannerShows(view, step)) return null;
  if (view.surfaced !== step && view.surfaced !== null) {
    return (
      <EarlierChanges
        view={view}
        surfaced={view.surfaced}
        taskId={taskId}
        className={className}
      />
    );
  }
  const first = here[0];
  const held = view.steps.length > 1;
  const count = here.length;
  return (
    <Panel
      role="region"
      aria-labelledby={id}
      pad="snug"
      className={cn("border-primary/20 shadow-lg", className)}
    >
      <div className="flex min-h-7 items-center gap-2.5">
        <IconTile tone="alert" shape="round" size="xs" numeral={count} />
        <b id={id} className="whitespace-nowrap text-[13px]">
          {`${plural(count, "suggested change")} to review`}
        </b>
        <span
          title={view.trigger_said ?? undefined}
          className="min-w-0 flex-1 truncate text-xs text-muted-foreground"
        >
          {first.trigger_said}
        </span>
        <Button
          variant="link"
          size="dense"
          className="px-1.5"
          aria-expanded={open}
          onClick={() => {
            setOpen((was) => !was);
          }}
        >
          {open ? "Hide list" : "Show all"}
        </Button>
        <Button
          size="dense"
          onClick={() => {
            onJump(first.action_id);
          }}
        >
          Next change
        </Button>
      </div>
      {open && <BannerList here={here} held={held} onJump={onJump} />}
    </Panel>
  );
}
